/**
 * S3 Service
 *
 * Handles S3 file operations via presigned URLs from backend Lambda.
 * Replaces AWS Amplify Storage functionality.
 */

import api from './api';
import { getUserIdentityId } from '../auth';

/**
 * Upload file to S3 using presigned URL
 *
 * @param {File} file - File object to upload
 * @param {string} key - S3 object key (path)
 * @param {string} contentType - MIME type
 * @returns {Promise<string>} - S3 key of uploaded file
 */
export const uploadFile = async (file, key, contentType) => {
  try {
    console.log(`Requesting presigned URL for upload: ${key}`);
    console.log('Request payload:', { key, contentType });

    // Request presigned PUT URL from backend
    const response = await api.post('/s3/presign-upload', {
      key,
      contentType,
    });

    console.log('Presign response:', response);
    const { data } = response;

    if (!data || !data.url) {
      console.error('Invalid response from presign-upload:', data);
      throw new Error('No presigned URL returned from backend');
    }

    console.log(`Uploading file to S3: ${key}`);

    // Upload file directly to S3 using presigned URL
    const uploadResponse = await fetch(data.url, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }

    console.log(`File uploaded successfully: ${key}`);
    return key;
  } catch (error) {
    console.error('File upload error:', error);
    throw error;
  }
};

/**
 * Get download URL for S3 object
 *
 * @param {string} key - S3 object key
 * @returns {Promise<string>} - Presigned GET URL
 */
export const getDownloadUrl = async (key) => {
  try {
    console.log(`Requesting presigned URL for download: ${key}`);

    const { data } = await api.post('/s3/presign-download', {
      key,
    });

    if (!data.url) {
      throw new Error('No presigned URL returned from backend');
    }

    return data.url;
  } catch (error) {
    console.error('Failed to get download URL:', error);
    throw error;
  }
};

/**
 * List objects in S3 prefix
 *
 * @param {string} prefix - S3 prefix to list
 * @returns {Promise<Object>} - Result object with items array
 */
export const listFiles = async (prefix) => {
  try {
    console.log(`Listing files with prefix: ${prefix}`);

    const { data } = await api.post('/s3/list', {
      prefix,
    });

    // Return in format compatible with Amplify list() response
    return {
      items: (data.items || []).map(item => ({
        path: item.key || item.path,
        lastModified: item.lastModified || item.LastModified,
        size: item.size || item.Size,
        eTag: item.eTag || item.ETag
      }))
    };
  } catch (error) {
    console.error('Failed to list files:', error);
    throw error;
  }
};

/**
 * Delete S3 object
 *
 * @param {string} key - S3 object key
 * @returns {Promise<void>}
 */
export const deleteFile = async (key) => {
  try {
    console.log(`Deleting file: ${key}`);

    await api.post('/s3/delete', {
      key,
    });

    console.log(`File deleted successfully: ${key}`);
  } catch (error) {
    console.error('Failed to delete file:', error);
    throw error;
  }
};

/**
 * Get user's S3 prefix based on identity ID
 *
 * @returns {Promise<string>} - User's S3 prefix (e.g., "users/{identityId}/")
 */
export const getUserPrefix = async () => {
  const identityId = await getUserIdentityId();
  if (!identityId) {
    throw new Error('No user identity ID available');
  }
  return `users/${identityId}/`;
};

/**
 * Upload file to user-scoped S3 path
 *
 * @param {File} file - File object
 * @param {string} subPath - Path within user directory (e.g., "fusion_json_zip_sfn/file.zip")
 * @param {string} contentType - MIME type
 * @returns {Promise<string>} - Full S3 key
 */
export const uploadUserFile = async (file, subPath, contentType) => {
  const userPrefix = await getUserPrefix();
  const fullKey = `${userPrefix}${subPath}`;
  return uploadFile(file, fullKey, contentType);
};

/**
 * List files in user-scoped S3 path
 *
 * @param {string} subPath - Path within user directory (e.g., "fusion_json_zip_sfn/")
 * @returns {Promise<Array>} - Array of S3 objects
 */
export const listUserFiles = async (subPath = '') => {
  const userPrefix = await getUserPrefix();
  const fullPrefix = `${userPrefix}${subPath}`;
  return listFiles(fullPrefix);
};

/**
 * Get download URL for user-scoped file
 *
 * @param {string} subPath - Path within user directory
 * @returns {Promise<string>} - Presigned GET URL
 */
export const getUserFileUrl = async (subPath) => {
  const userPrefix = await getUserPrefix();
  const fullKey = `${userPrefix}${subPath}`;
  return getDownloadUrl(fullKey);
};

/**
 * Delete user-scoped file
 *
 * @param {string} subPath - Path within user directory
 * @returns {Promise<void>}
 */
export const deleteUserFile = async (subPath) => {
  const userPrefix = await getUserPrefix();
  const fullKey = `${userPrefix}${subPath}`;
  return deleteFile(fullKey);
};
